import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, Heart, Moon, Sun, Phone } from "lucide-react";
import { CartButton } from "@/components/shop/CartButton";
import { Link, useLocation } from "react-router-dom";
import { services } from "@/data/services";
import { useTheme } from "@/contexts/ThemeContext";
import { HolidayBanner } from "@/components/marketing/HolidayBanner";
import { VacationBanner } from "@/components/marketing/VacationBanner";
import logoLight from "@/assets/logo-light-floral-designs.png";
import logoDark from "@/assets/logo-wide-floral-designs.png";

const navLinks = [
  { name: "Home", href: "/" },
  { name: "About Kim", href: "/about" },
  { name: "Gallery", href: "/portfolio" },
  { name: "Shop", href: "/shop" },
  { name: "Service Areas", href: "/service-areas" },
  { name: "Contact", href: "/contact" },
  { name: "Request Proposal", href: "/floral-questionnaire" },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      {/* Vacation mode — takes priority when Kim is out of town */}
      <VacationBanner />
      {/* Auto-rotating holiday announcement (changes by date) */}
      <HolidayBanner />
      {/* Announcement banner — Call Kim emphasis */}
      <a
        href="tel:+14056273557"
        className="block bg-primary text-primary-foreground border-b border-primary/30 py-2 hover:bg-primary/90 transition-colors group"
        aria-label="Call Kim now at (405) 627-3557"
      >
        <p className="text-center text-xs sm:text-sm font-medium flex items-center justify-center gap-2 tracking-wide px-3">
          <Phone size={14} className="opacity-90" />
          <span className="font-semibold">Call Kim now: (405) 627-3557</span>
          <span className="hidden md:inline opacity-90">· Same-week consultations available</span>
        </p>
      </a>
      <div className="bg-[#F8F5F2] dark:bg-[#1A1412]/95 backdrop-blur-lg border-b border-foreground/10 dark:border-[rgba(255,220,200,0.08)] shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" className="flex items-center py-2 -my-4 lg:-my-6">
            <img src={theme === "light" ? logoLight : logoDark} alt="Floral Designs By Kim" width={200} height={48} className="h-[90px] lg:h-[110px] w-auto drop-shadow-sm relative z-10" />
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            <Link to="/" className="link-underline text-foreground/70 dark:text-foreground/70 hover:text-foreground dark:hover:text-white transition-colors font-medium pb-1">
              Home
            </Link>

            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="link-underline !inline-flex items-center gap-1 text-foreground/70 dark:text-foreground/70 hover:text-foreground dark:hover:text-white transition-colors font-medium pb-1"
              >
                Services
                <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[680px] bg-card border border-border rounded-2xl shadow-2xl p-6"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      {services.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors group"
                        >
                          <div className="w-9 h-9 rounded-lg bg-brand-surface text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors flex-shrink-0">
                            <s.icon size={18} />
                          </div>
                          <span className="text-sm font-medium text-foreground">{s.title}</span>
                        </Link>
                      ))}
                    </div>
                    <div className="border-t border-border mt-4 pt-4">
                      <Link
                        to="/services"
                        className="text-sm font-medium text-primary hover:text-brand-text transition-colors flex items-center gap-1"
                      >
                        View All Services <ChevronDown size={14} className="-rotate-90" />
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/about" className="link-underline text-foreground/70 dark:text-foreground/70 hover:text-foreground dark:hover:text-white transition-colors font-medium pb-1">
              About Kim
            </Link>
            <Link to="/portfolio" className="link-underline text-foreground/70 dark:text-foreground/70 hover:text-foreground dark:hover:text-white transition-colors font-medium pb-1">
              Gallery
            </Link>
            <Link to="/shop" className="link-underline text-foreground/70 dark:text-foreground/70 hover:text-foreground dark:hover:text-white transition-colors font-medium pb-1">
              Shop
            </Link>
            <Link to="/service-areas" className="link-underline text-foreground/70 dark:text-foreground/70 hover:text-foreground dark:hover:text-white transition-colors font-medium pb-1">
              Service Areas
            </Link>
            <Link to="/contact" className="link-underline text-foreground/70 dark:text-foreground/70 hover:text-foreground dark:hover:text-white transition-colors font-medium pb-1">
              Contact
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-foreground/60 hover:text-foreground hover:bg-accent transition-colors"
              aria-label="Toggle theme"
            >
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <CartButton />
            <Link to="/contact">
              <Button className="bg-primary hover:bg-primary/90 text-white glow-primary btn-shine">
                Book Consultation
              </Button>
            </Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-foreground/60 hover:text-foreground hover:bg-accent transition-colors"
              aria-label="Toggle theme"
            >
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <CartButton />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-foreground"
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      </div>
      <div className="h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent" />

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#F8F5F2] dark:bg-[#1A1412] border-t border-foreground/10 overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
              <Link to="/" className="text-foreground/70 hover:text-foreground dark:text-foreground/70 dark:hover:text-white transition-colors font-medium py-3">
                Home
              </Link>

              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="flex items-center justify-between text-foreground/70 hover:text-foreground dark:text-foreground/70 dark:hover:text-white transition-colors font-medium py-3"
              >
                Services
                <ChevronDown size={16} className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {mobileServicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pl-4 flex flex-col gap-1 pb-2">
                      {services.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          className="flex items-center gap-3 text-foreground/60 hover:text-foreground dark:text-foreground/60 dark:hover:text-white transition-colors py-2 text-sm"
                        >
                          <s.icon size={16} />
                          {s.title}
                        </Link>
                      ))}
                      <Link
                        to="/services"
                        className="text-primary hover:text-brand-text transition-colors py-2 text-sm font-medium"
                      >
                        View All Services →
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <Link to="/about" className="text-foreground/70 hover:text-foreground dark:text-foreground/70 dark:hover:text-white transition-colors font-medium py-3">
                About Kim
              </Link>
              <Link to="/portfolio" className="text-foreground/70 hover:text-foreground dark:text-foreground/70 dark:hover:text-white transition-colors font-medium py-3">
                Gallery
              </Link>
              <Link to="/shop" className="text-foreground/70 hover:text-foreground dark:text-foreground/70 dark:hover:text-white transition-colors font-medium py-3">
                Shop
              </Link>
              <Link to="/service-areas" className="text-foreground/70 hover:text-foreground dark:text-foreground/70 dark:hover:text-white transition-colors font-medium py-3">
                Service Areas
              </Link>
              <Link to="/contact" className="text-foreground/70 hover:text-foreground dark:text-foreground/70 dark:hover:text-white transition-colors font-medium py-3">
                Contact
              </Link>

              <div className="pt-4 border-t border-foreground/10">
                <Link to="/contact">
                  <Button className="w-full bg-primary hover:bg-primary/90">
                    Book Consultation
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
